import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { UnfoldMore, UnfoldLess } from '@mui/icons-material';
import { Group, Panel } from 'react-resizable-panels';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import {
  useGetSeasonsQuery,
  useGetScheduledFlightsQuery,
  useGetAirportsQuery,
} from 'features/apiSlice';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import StyledButton from 'components/StyledButton';
import ResizeSeparator from 'components/ResizeSeparator';
import StationSidebar from './StationSidebar';
import StationTimeline from './StationTimeline';
import { DAY_LABELS, AC_PALETTE, DEF_HOUR_W, DEF_ROW_H } from './constants';


const StationView = () => {
  useAuthCheck();

  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [selectedStation, setSelectedStation]   = useState('');
  const [filterDay, setFilterDay]               = useState('1');
  const [rowHeight, setRowHeight]               = useState(DEF_ROW_H);
  const [hourWidth, setHourWidth]               = useState(DEF_HOUR_W);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: seasonsData } = useGetSeasonsQuery({ limit: 200 });
  const seasons = useMemo(() => seasonsData?.data || [], [seasonsData]);

  useEffect(() => {
    if (!selectedSeasonId && seasons.length > 0) {
      const active = seasons.find((s) => s.status === 'active') || seasons[0];
      if (active) setSelectedSeasonId(active._id);
    }
  }, [seasons, selectedSeasonId]);

  const { data: flightData } = useGetScheduledFlightsQuery(
    { seasonId: selectedSeasonId, limit: 2000 },
    { skip: !selectedSeasonId },
  );
  const allFlights = useMemo(() => flightData?.data || [], [flightData]);

  const { data: airportsData } = useGetAirportsQuery();
  const airportMap = useMemo(
    () => Object.fromEntries((airportsData || []).map((a) => [a.iataCode, a])),
    [airportsData],
  );

  // Airports from API - used for the station selector dropdown
  const stationOptions = useMemo(
    () => (airportsData || []).filter((a) => a.iataCode).sort((a, b) => a.iataCode.localeCompare(b.iataCode)),
    [airportsData],
  );

  // Airports that have flights in this season - used for sidebar
  const stations = useMemo(() => {
    const set = new Set();
    for (const f of allFlights) {
      if (f.dep) set.add(f.dep);
      if (f.arr) set.add(f.arr);
    }
    return [...set].sort();
  }, [allFlights]);

  useEffect(() => {
    if (!selectedStation && stations.length > 0) setSelectedStation(stations[0]);
  }, [stations, selectedStation]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const acTypeColorMap = useMemo(() => {
    const types = [...new Set(allFlights.map((f) => f.acType).filter(Boolean))];
    return Object.fromEntries(types.map((t, i) => [t, AC_PALETTE[i % AC_PALETTE.length]]));
  }, [allFlights]);

  const dayFlights = useMemo(() => allFlights.filter((f) => {
    if (f.status === 'cancelled') return false;
    if (!f.daysOfOperation?.includes(filterDay)) return false;
    return true;
  }), [allFlights, filterDay]);

  const stationMovements = useMemo(() => {
    if (!selectedStation) return { dep: [], arr: [] };
    return {
      dep: dayFlights.filter((f) => f.dep === selectedStation).sort((a, b) => (a.std || '').localeCompare(b.std || '')),
      arr: dayFlights.filter((f) => f.arr === selectedStation).sort((a, b) => (a.sta || '').localeCompare(b.sta || '')),
    };
  }, [dayFlights, selectedStation]);

  const stationStats = useMemo(() => {
    const { dep, arr } = stationMovements;
    return { dep: dep.length, arr: arr.length };
  }, [stationMovements]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Station View" 
          subtitle="Airport Schedule" 
        />

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">          
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Chip label={`DEP: ${stationStats.dep}`} size="small" color="primary" variant="outlined" />
            <Chip label={`ARR: ${stationStats.arr}`} size="small" color="success" variant="outlined" />
          </Stack>

          <TextField
            size="small"
            select
            label="Season"
            value={selectedSeasonId}
            onChange={(e) => setSelectedSeasonId(e.target.value)}
            slotProps={{
              select: {
                renderValue: (selected) =>
                  seasons.find((s) => s._id === selected)?.seasonCode || '',
              },
            }}
          >
            {seasons.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {s.seasonCode} - {s.seasonName}
              </MenuItem>
            ))}            
          </TextField>
          
          {/* Row height */}
          <StyledButton onClick={() => setRowHeight((p) => Math.max(20, p - 4))}>
            <UnfoldLess />
          </StyledButton>
          <StyledButton onClick={() => setRowHeight((p) => Math.min(80, p + 4))}>
            <UnfoldMore />
          </StyledButton>

          {/* Horizontal zoom */}
          <StyledButton sx={{ transform: 'rotate(90deg)' }} onClick={() => setHourWidth((p) => Math.max(20, p - 8))}>
            <UnfoldLess />
          </StyledButton>
          <StyledButton sx={{ transform: 'rotate(90deg)' }} onClick={() => setHourWidth((p) => Math.min(140, p + 8))}>
            <UnfoldMore />
          </StyledButton>
        </Box>
      </FlexBetween>

      <Box 
        display="grid" 
        gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
        gap={2}
      >
        <Box
          sx={{
            gridColumn: 'span 12',
            height: LAYOUT_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {/* Filters */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <TextField
              size="small"
              select
              label="Station"
              value={selectedStation}
              onChange={(e) => setSelectedStation(e.target.value)}
              slotProps={{
                select: { renderValue: (v) => v },
              }}
              sx={{ width: 90 }}
            >
              {stationOptions.map((ap) => (
                <MenuItem key={ap.iataCode} value={ap.iataCode} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ fontWeight: 600, fontSize: '0.85rem', fontFamily: 'monospace' }}>
                    {ap.iataCode}
                  </Box>
                  {ap.airportName && (
                    <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2 }}>
                      {ap.airportName}
                    </Box>
                  )}
                </MenuItem>
              ))}
            </TextField>

            <ToggleButtonGroup
              exclusive 
              size="small" 
              value={filterDay}
              onChange={(_, v) => v && setFilterDay(v)}
            >
              {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                <ToggleButton 
                key={d} 
                value={String(d)} 
                sx={{ px: 1.5, fontSize: '0.7rem' }}
                >
                  {DAY_LABELS[d].slice(0, 2)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Main - resizable sidebar + timeline */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Group direction="horizontal" style={{ height: '100%' }}>
              <Panel
                defaultSize={15}
                minSize={0}
              >
                <StationSidebar
                  stations={stations}
                  selectedStation={selectedStation}
                  onSelect={setSelectedStation}
                  dayFlights={dayFlights}
                  airportMap={airportMap}
                />
              </Panel>

              <ResizeSeparator gripVisible={false} />

              <Panel
                defaultSize={85}
                minSize={0}
              >
                <StationTimeline
                  selectedStation={selectedStation}
                  airportMap={airportMap}
                  filterDay={filterDay}
                  stationMovements={stationMovements}
                  acTypeColorMap={acTypeColorMap}
                  rowHeight={rowHeight}
                  hourWidth={hourWidth}
                />
              </Panel>
            </Group>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default StationView;
